import { useQuery, QueryObserverResult, UseQueryOptions } from "react-query";

import type {
  FeatureFlagsAppName as AppName,
  FeatureFlagsDataEnvelope,
  FeatureFlagsNetworkResponse,
} from "../../../models/featureFlags";
import { FetcherQueryKey } from "../../../services/query";
import { getGranularRuntimeEnvName } from "../../../helpers/url";
import { getCountryFromUrl } from "../../../i18n";

import { useMaybeUserId } from "../userId";
import { useMaybeCurrentOperationId } from "../operations";

import {
  FeatureFlagsFetcherParams,
  featureFlagsQueryKeyPrefix,
  computeIsQueryEnabled,
  computeFeatureFlagsQueryKey,
  featureFlagsFetcher,
  computeResultOfUseQuery,
  getContainerNameFromUrl,
  deleteAllLaunchDarklyInstances,
  useCleanupMemoization,
} from "./featureFlags.query.helpers";

export {
  featureFlagsQueryKeyPrefix,
  computeFeatureFlagsQueryKey,
  deleteAllLaunchDarklyInstances,
};

// These come from environment variables or from the hostname
// so these can safely be considered **constant**
// throughout the lifetime of the app.
const containerName = getContainerNameFromUrl();
const granularRuntimeEnvName = getGranularRuntimeEnvName();
const country = getCountryFromUrl();

// ##########################
// ## useFeatureFlagsQuery ##
// ##########################

export type ParamsOfUseFeatureFlagsQuery = {
  app: AppName;
  ignoreUserId?: boolean;
  ignoreOperationId?: boolean;
  watch?: UseQueryOptions["notifyOnChangeProps"];
  forceDisable?: boolean;
};
export type ResultOfUseFeatureFlagsQuery = QueryObserverResult<
  FeatureFlagsDataEnvelope,
  Error
>;
export type UseFeatureFlagsQuery = (
  params: ParamsOfUseFeatureFlagsQuery,
) => ResultOfUseFeatureFlagsQuery;

/**
 * The returned value of this hook will contain the React Query result
 * of the fetching of the feature flags data.
 *
 * This hook is only meant for use by the hooks in `./featureFlags.ts`.
 * Do not use this hook outside these hooks as this could couple us to React Query.
 */
export const useFeatureFlagsQuery: UseFeatureFlagsQuery = ({
  app,
  ignoreUserId = false,
  ignoreOperationId = false,
  watch,
  forceDisable,
}: ParamsOfUseFeatureFlagsQuery): ResultOfUseFeatureFlagsQuery => {
  const currentUserId = useMaybeUserId();
  const currentOperationId = useMaybeCurrentOperationId();

  // Use the currentUserId and currentOperationId, unless the caller
  // has explicitly requested that they be ignored.
  let userId: string | null = currentUserId ?? null;
  if (ignoreUserId) {
    userId = null;
  }
  let operationId: string | null = currentOperationId ?? null;
  if (ignoreOperationId) {
    operationId = null;
  }

  const params: FeatureFlagsFetcherParams = {
    appName: app,
    containerName,
    granularRuntimeEnvName,
    country,
    userId,
    operationId,
  };
  const queryKey = computeFeatureFlagsQueryKey(params);
  const resultOfUseQuery = useQuery<
    FeatureFlagsNetworkResponse,
    Error,
    FeatureFlagsNetworkResponse,
    FetcherQueryKey<FeatureFlagsFetcherParams>
  >(queryKey, featureFlagsFetcher, {
    enabled: computeIsQueryEnabled(params, {
      ignoreUserId,
      ignoreOperationId,
      forceDisable,
    }),
    notifyOnChangeProps: watch || "tracked",
    // We never remove feature flags data once fetched because we cannot revalidate feature flags data
    // because existing functionality unfortunately does not support reactive feature flags.
    cacheTime: Infinity,
  });

  useCleanupMemoization(queryKey);

  return computeResultOfUseQuery(resultOfUseQuery);
};
